import React,{ useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import api from "../../api.js";
import "../../CSS/RateDefinition.css";
import { useRights } from "../../Components/RightsContext.jsx";
//
// Must match this screen's Submenus.pathname exactly — verify against the
// DB before trusting this (a mismatched string silently hides everything).
const RATE_DEFINITION_PATH = "Master/RateDefinition";

/* ─── Constants ─── */
const CURR = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" };

const UOM_OPTIONS = ["KG", "LB", "OZ", "CBM", "CFT", "MT"];

/* ─── Helpers ─── */
let _rowId = 0;
const nextId = () => ++_rowId;

function makeRow(fw = "", tw = "", uom = "KG", rate = "", addlWt = "", addlRate = "") {
  return { id: nextId(), fw, tw, uom, rate, addlWt, addlRate };
}

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

//helper function to handle pagination button when data is large
function getPaginationRange(current, total, delta = 2) {
  const range = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  range.push(1);
  if (left > 2) range.push("...");
  for (let i = left; i <= right; i++) range.push(i);
  if (right < total - 1) range.push("...");
  if (total > 1) range.push(total);

  return range;
}
/* ─── Sub-components ─── */

function ChevronIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Card({ id, num, title, sub, children, collapsed, onToggle }) {
  return (
    <div className={`card${collapsed ? " collapsed" : ""}`} id={id}>
      <div className="card-head" onClick={onToggle}>
        <div className="card-head-left">
          <div className="section-num">{num}</div>
          <div>
            <div className="card-title">{title}</div>
            <div className="card-sub">{sub}</div>
          </div>
        </div>
        <div className="chevron"><ChevronIcon /></div>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}
/* ─── AutoComplete / ComboBox — matches ConsignmentBooking style ─── */
function ComboBox({ id, value, onChange, options = [], placeholder = "Search…", className = "", disabled = false, valueKey = "value", labelKey = "label" }) {
  const [open,  setOpen]  = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [focIdx,setFocIdx]= React.useState(-1);
  const wrapRef  = React.useRef(null);
  const inputRef = React.useRef(null);

   const dropListRef = React.useRef(null);

  React.useEffect(() => {
  if (!open || focIdx < 0 || !dropListRef.current) return;
  const el = dropListRef.current.querySelector(`[data-idx="${focIdx}"]`);
  if (el) el.scrollIntoView({ block: "nearest" });
}, [focIdx, open]);

  // Normalise to { value, label } — case-insensitive key lookup
  const opts = React.useMemo(() => options.map(o => {
    const keys = Object.keys(o);
    const vKey = keys.find(k => k.toLowerCase() === valueKey.toLowerCase()) || valueKey;
    const lKey = keys.find(k => k.toLowerCase() === labelKey.toLowerCase()) || labelKey;
    return {
      value: String(o[vKey] ?? o.value ?? o.id ?? ""),
      label: String(o[lKey] ?? o.label ?? o.name ?? ""),
    };
  }), [options, valueKey, labelKey]);

  const selected = opts.find(o => o.value === String(value ?? ""));

  // Highlight matching text
  const highlight = (text, q) => {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return <>{text.slice(0, idx)}<mark>{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>;
  };

  const filtered = query.trim()
    ? opts.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : opts;

  // Close on outside click
  React.useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) closeBox(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openBox = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setFocIdx(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const closeBox = () => { setOpen(false); setQuery(""); setFocIdx(-1); };

  const pick = val => {
    onChange({ target: { value: val } });
    closeBox();
  };

  const handleKey = e => {
    if (e.key === "ArrowDown") { e.preventDefault(); setFocIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (focIdx >= 0 && filtered[focIdx]) pick(filtered[focIdx].value); else if (filtered.length === 1) pick(filtered[0].value); }
    else if (e.key === "Escape") closeBox();
  };

  // Use fixed positioning so dropdown escapes any parent overflow:hidden
  const [dropStyle, setDropStyle] = React.useState({});
  const triggerRef = React.useRef(null);

  const updateDropPos = React.useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setDropStyle({
      position: "fixed",
      top: r.bottom + 4,
      left: r.left,
      width: r.width,
      zIndex: 99999,
    });
  }, []);


  React.useEffect(() => {
    if (open) {
      updateDropPos();
      window.addEventListener("scroll", updateDropPos, true);
      window.addEventListener("resize", updateDropPos);
    }
    return () => {
      window.removeEventListener("scroll", updateDropPos, true);
      window.removeEventListener("resize", updateDropPos);
    };
  }, [open, updateDropPos]);

  return (
    <div className="cb-ac" ref={wrapRef} id={id}>
      <div ref={triggerRef} className={`cb-ac-box${open ? " open" : ""}${disabled ? " cb-disabled" : ""}`} onClick={openBox}>
        <input
          ref={inputRef}
          className={`cb-ac-in${className ? " " + className : ""}`}
          type="text"
          placeholder={selected ? selected.label : placeholder}
          value={open ? query : (selected ? selected.label : "")}
          onChange={e => { setQuery(e.target.value); setFocIdx(-1); }}
          onKeyDown={handleKey}
          readOnly={!open}
          disabled={disabled}
          style={{ cursor: disabled ? "not-allowed" : open ? "text" : "pointer" }}
        />
        {selected && !open && (
          <span className="cb-ac-x on" onMouseDown={e => { e.stopPropagation(); pick(""); }}>×</span>
        )}
        <span className="cb-ac-chev">▾</span>
      </div>

      {/* {open && ReactDOM.createPortal(
        <div className="cb-ac-drop open" style={dropStyle}>
          {filtered.length === 0
            ? <div className="cb-ac-empty">No results found</div>
            : filtered.map((o, i) => (
              <div
                key={o.value}
                className={`cb-ac-opt${o.value === String(value ?? "") ? " sel" : ""}${i === focIdx ? " foc" : ""}`}
                onMouseDown={e => { e.preventDefault(); pick(o.value); }}
              >
                {highlight(o.label, query)}
              </div>
            ))
          }
        </div>,
        document.body
      )} */}
      {open && ReactDOM.createPortal(
        <div className="cb-ac-drop open" style={dropStyle} ref={dropListRef}>
          {filtered.length === 0
            ? <div className="cb-ac-empty">No results found</div>
            : filtered.map((o, i) => (
              <div
                key={o.value}
                data-idx={i}
                className={`cb-ac-opt${o.value === String(value ?? "") ? " sel" : ""}${i === focIdx ? " foc" : ""}`}
                onMouseDown={e => { e.preventDefault(); pick(o.value); }}
              >
                {highlight(o.label, query)}
              </div>
            ))
          }
        </div>,
        document.body
      )}
    </div>
  );
}

// CitySelect alias for booking/delivery place fields
function CitySelect({ id, required, value, onChange, options, placeholder, className, disabled = false }) {
  return (
    <ComboBox
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder || "Search…"}
      className={className}
      disabled={disabled}
      valueKey="place_id"
      labelKey="place_Name"
    />
  );
}


/* ─── Main Component ─── */
export default function RateDefinition() {
  const rights = useRights(RATE_DEFINITION_PATH);
  const [view, setView] = useState("search"); // "form" | "search"

  // ── Search window state ──
  const [searchFilters, setSearchFilters] = useState({ party: "", place: "", status: "" });
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const searchPageSize = 10;

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchFilters.party)  params.append("party",  searchFilters.party);
      if (searchFilters.place)  params.append("place",  searchFilters.place);
      if (searchFilters.status) params.append("status", searchFilters.status);
      const res = await api.get(`/GetRates?${params.toString()}`);
      setSearchResults(Array.isArray(res.data) ? res.data : []);
      setSearchPage(1);
    } catch (err) {
      console.error("Search error:", err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // "new" | "edit" | "view"
  const [formMode, setFormMode] = useState("new");
  const [editingRateId, setEditingRateId] = useState(null);

  // Map a Rate+RateDetails API response into all form state
  const loadRateIntoForm = (rateData, rateDetails, mode) => {
    // API returns camelCase — use camelCase keys throughout
    // e.g. rateData.status, rateData.currency_Id, rateData.party_Id etc.

    // ── Status / dates ──
    setActive(rateData.status === "A");
    setEffFrom(rateData.effective_From ? rateData.effective_From.split("T")[0] : todayISO());
    setEffTo(rateData.effective_To     ? rateData.effective_To.split("T")[0]   : "");

    // ── Currency ──
    const currObj = currencies.find(c => c.Currency_Id === rateData.currency_Id);
    const currCode = currObj ? currObj.Currency_Code   : "INR";
    const currSymV = currObj ? currObj.Currency_Symbol : "₹";
    setCurrency(currCode);
    setCurSym(currSymV);

    // ── Validity ──
    const valObj = validities.find(v => v.Validity_Id === rateData.rate_ValID);

    // ── Service type ──
    const srvObj = serviceTypes.find(s => s.Service_id === rateData.services_Id);

    // ── Delivery type ──
    const dlvObj = deliveryTypes.find(d => d.Del_Type_id === rateData.del_TypeId);

    // ── Transport ──
    const motObj = transportModes.find(t => t.Transport_Id === rateData.mod_of_Transport);

    // ── Priority ──
    const priorityMap = { 1: "normal", 2: "urgent", 3: "overnight", 4: "sameday" };
    const priorityVal = priorityMap[rateData.priority_id] || "";

    // Load parties for branch, jobs for party when editing/viewing
    if (rateData.branch_Id) {
      api.get(`/dropdown?mode=bPRT&branchId=${rateData.branch_Id}`)
        .then(r => setParties(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    }
    if (rateData.party_Id) {
      api.get(`/dropdown?mode=BJOB&partyId=${rateData.party_Id}`)
        .then(r => setJobs(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    }

    setFields({
      branch:        rateData.branch_Id      ? String(rateData.branch_Id)     : "",
      party:         rateData.party_Id       ? String(rateData.party_Id)      : "",
      job:           rateData.job_Id         ? String(rateData.job_Id)        : "",
      validity:      valObj  ? valObj.Validity_Code        : "",
      rateRemarks:   rateData.rate_Remark   || "",
      bookingPlace:  rateData.book_PlaceID  ? String(rateData.book_PlaceID)   : "",
      deliveryPlace: rateData.del_PlaceID   ? String(rateData.del_PlaceID)    : "",
      zone:          rateData.zone_id       ? String(rateData.zone_id)        : "",
      routeNote:     rateData.route_Note    || "",
      serviceType:   srvObj ? srvObj.Service_Code          : "",
      deliveryType:  dlvObj ? dlvObj.Delivery_Type_Code    : "",
      transport:     motObj ? motObj.Transport_Code        : "",
      priority:      priorityVal,
      minWt:         rateData.min_Charg_Wgt != null ? String(rateData.min_Charg_Wgt) : "",
      handling:      rateData.instruction   || "",
    });

    setErrors({});

    // ── Weight slabs — rateDetails also comes back camelCase ──
    _rowId = 0;
    if (Array.isArray(rateDetails) && rateDetails.length > 0) {
      const uomMap = {};
      uomOptions.forEach(u => { uomMap[u.Uom_Id] = u.UOM_Code; });
      const mapped = rateDetails.map(d =>
        makeRow(
          d.start_Range != null ? String(d.start_Range) : "",
          d.end_Range   != null ? String(d.end_Range)   : "",
          uomMap[d.uom_Id]     || "KG",
          d.rate_Amount != null ? String(d.rate_Amount) : "",
          d.add_Weight  != null ? String(d.add_Weight)  : "",
          ""
        )
      );
      setSlabs(mapped);
    } else {
      setSlabs([makeRow()]);
    }
    setNewSlab({ fw: "", tw: "", uom: "", rate: "", addlWt: "", addlRate: "" });
  };

  const handleViewRate = async (rate) => {
    try {
      const res = await api.get(`/GetRateById?id=${rate.rate_Def_Id}`);
      const { rateHeader, rateDetails } = res.data;
      setFormMode("view");
      setEditingRateId(rate.rate_Def_Id);
      loadRateIntoForm(rateHeader, rateDetails, "view");
      setView("form");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Load rate error:", err);
      window.alert("Failed to load rate details.");
    }
  };

  const handleEditRate = async (rate) => {
    try {
      const res = await api.get(`/GetRateById?id=${rate.rate_Def_Id}`);
      const { rateHeader, rateDetails } = res.data;
      setFormMode("edit");
      setEditingRateId(rate.rate_Def_Id);
      loadRateIntoForm(rateHeader, rateDetails, "edit");
      setView("form");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Load rate error:", err);
      window.alert("Failed to load rate details.");
    }
  };

  const [rateSeq, setRateSeq] = useState("2025-0001");
  const [collapsed, setCollapsed] = useState({ c1: false, c2: false, c3: false, c4: false });
  const [active, setActive] = useState(true);
  const [effFrom, setEffFrom] = useState(todayISO());
  // const [effTo, setEffTo] = useState("");
  const [effTo, setEffTo] = useState(todayISO());
  const [currency, setCurrency] = useState("INR");
  const [curSym, setCurSym] = useState("₹");
  const [slabs, setSlabs] = useState(() => [makeRow()]);
  // ── Sticky floating alert ──
  const [alertMsg,  setAlertMsg]  = useState("");
  const [alertKind, setAlertKind] = useState("success"); // "success" | "error" | "warn"
  const [alertShow, setAlertShow] = useState(false);
  const alertTimer = React.useRef(null);

  const showStickyAlert = () => {
    setAlertShow(true);
    if (alertTimer.current) clearTimeout(alertTimer.current);
    alertTimer.current = setTimeout(() => setAlertShow(false), 5000);
  };
  const [errors, setErrors] = useState({});

  // const [bookingPlaces, setBookingPlaces] = useState([]);
  // const [deliveryPlaces, setDeliveryPlaces] = useState([]);
  const [places, setPlaces] = useState([]);
  const [zones, setZones] = useState([]);
  const [branches, setBranches] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [partyTypes, setPartyTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [validities, setValidities] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [transportModes, setTransportModes] = useState([]);
  const [parties, setParties] = useState([]);
  const [allParties, setAllParties] = useState([]); // for search window
  const [uomOptions, setUomOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // const [resBRC, resDES, resZone, resPTY, resCURR, resVAL, resSRV, resDLV, resMOT, resPMS, resUOM] = await Promise.all([
        //   api.get('/dropdown?mode=BRC'),
        //   api.get('/dropdown?mode=DES'),
        const [resPLC, resZone, resPTY, resCURR, resVAL, resSRV, resDLV, resMOT, resBLB, resUOM] = await Promise.all([
          api.get('/dropdown?mode=plc'),
          api.get('/dropdown?mode=zone'),
          api.get('/dropdown?mode=PTY'),
          api.get('/dropdown?mode=CURR'),
          api.get('/dropdown?mode=VAL'),
          api.get('/dropdown?mode=SRV'),
          api.get('/dropdown?mode=DLV'),
          api.get('/dropdown?mode=MOT'),
          api.get('/dropdown?mode=BLB'),
          api.get('/dropdown?mode=UOM')
        ]);
        setPlaces(resPLC.data);
        setZones(resZone.data);
        setPartyTypes(resPTY.data);
        setCurrencies(resCURR.data);
        setValidities(resVAL.data);
        setServiceTypes(resSRV.data);
        setDeliveryTypes(resDLV.data);
        setTransportModes(resMOT.data);
        setBranches(resBLB.data);
        setUomOptions(resUOM.data);
        // parties and jobs loaded on cascade
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };
    fetchData();

    // Load all parties for search window filter (independent of cascade)
    api.get('/dropdown?mode=bprty').then(r => {
      if (Array.isArray(r.data)) setAllParties(r.data);
    }).catch(() => {});
  }, []);

  // Form field state
  const [fields, setFields] = useState({
    party: "",  validity: "", rateRemarks: "",
    bookingPlace: "", deliveryPlace: "", zone: "", routeNote: "",
    serviceType: "", deliveryType: "", transport: "", priority: "", minWt: "", handling: "",
  });

  /* summary derived values */
  const summary = (() => {
    const n = slabs.length;
    let minFW = Infinity, maxTW = -Infinity, rates = [], uom = "";
    slabs.forEach(r => {
      const fw = parseFloat(r.fw) || 0;
      const tw = parseFloat(r.tw) || 0;
      const rt = parseFloat(r.rate) || null;
      if (fw < minFW) minFW = fw;
      if (tw > maxTW) { maxTW = tw; uom = r.uom; }
      if (rt) rates.push(rt);
    });
    return {
      count: n,
      range: minFW === Infinity ? "—" : `${minFW} – ${maxTW === -Infinity ? "—" : maxTW + " " + uom}`,
      low: rates.length ? curSym + Math.min(...rates).toLocaleString() : "—",
      high: rates.length ? curSym + Math.max(...rates).toLocaleString() : "—",
    };
  })();

  /* helpers */
  const toggleCard = (id) =>
    setCollapsed(p => ({ ...p, [id]: !p[id] }));

  const handleCurrency = (val) => {
    setCurrency(val);
    const found = currencies.find(c => c.Currency_Code === val);
    setCurSym(found ? found.Currency_Symbol : (CURR[val] || "₹"));
  };

  // ── Cascade: Branch → Parties ──
  const handleBranchChange = async (e) => {
    const branchId = e.target.value;
    setFields(p => ({ ...p, branch: branchId, party: "", job: "" }));
    setParties([]);
    setJobs([]);
    if (!branchId) return;
    try {
      const res = await api.get(`/dropdown?mode=bPRT&branchId=${branchId}`);
      setParties(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error("Party fetch error:", err); }
  };

  // ── Cascade: Party → Jobs ──
  const handlePartyChange = async (e) => {
    const partyId = e.target.value;
    setFields(p => ({ ...p, party: partyId, job: "" }));
    if (errors.party) setErrors(p => ({ ...p, party: false }));
    setJobs([]);
    if (!partyId) return;
    try {
      const res = await api.get(`/dropdown?mode=BJOB&partyId=${partyId}`);
      setJobs(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error("Job fetch error:", err); }
  };

  const handleToggle = (checked) => setActive(checked);

  const flashAlert = (type) => {
    if (type === "success") {
      setAlertMsg(formMode === "edit" ? "Rate updated successfully!" : "Rate definition saved successfully!");
      setAlertKind("success");
    } else {
      setAlertMsg("Please fill all required fields and add at least one weight slab.");
      setAlertKind("error");
    }
    showStickyAlert();
  };

  const handleField = (key) => (e) => {
    setFields(p => ({ ...p, [key]: e.target.value }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: false }));
  };

  /* slab operations */
  const [newSlab, setNewSlab] = useState({ fw: "", tw: "", uom: "", rate: "", addlWt: "", addlRate: "" });

  const moveFocusToNext = (currentTarget) => {
    const focusableSelector = '.sl-inp:not([disabled]):not([readonly]), .sl-sel:not([disabled]), .btn-add-slab';
    const elements = Array.from(document.querySelectorAll(focusableSelector)).filter(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    const index = elements.indexOf(currentTarget);
    if (index > -1 && index < elements.length - 1) {
      elements[index + 1].focus();
    }
  };

  const handleSlabEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      moveFocusToNext(e.target);
    }
  };

  const addSlab = () => {
    const from = Number(newSlab.fw);
    const to = Number(newSlab.tw);
    const rate = Number(newSlab.rate);
    const uom = newSlab.uom;

    // Required field checks
    if (!newSlab.fw || isNaN(from)) { window.alert("Enter From Weight."); return; }
    if (!newSlab.tw || isNaN(to)) { window.alert("Enter To Weight."); return; }
    if (!rate || isNaN(rate)) { window.alert("Enter Rate."); return; }
    if (!uom) { window.alert("Select UOM."); return; }

    // From > To check
    if (from > to) { window.alert("From Weight cannot be greater than To Weight."); return; }

    // Duplicate range check (same from, to, uom)
    const isDuplicate = slabs.some(r =>
      Number(r.fw) === from && Number(r.tw) === to && r.uom === uom
    );
    if (isDuplicate) { window.alert(`Slab from ${from} to ${to} (${uom}) already defined.`); return; }

    // Overlapping range check (for same UOM)
    for (const r of slabs) {
      if (r.uom !== uom) continue;
      const iFrom = Number(r.fw);
      const iTo = Number(r.tw);
      if (from <= iTo && to >= iFrom) {
        window.alert("Weight range overlaps with an existing slab."); return;
      }
    }

    setSlabs(p => [...p, makeRow(newSlab.fw, newSlab.tw, newSlab.uom, newSlab.rate, newSlab.addlWt, newSlab.addlRate)]);
    setNewSlab({ fw: "", tw: "", uom: "", rate: "", addlWt: "", addlRate: "" });
  };

  const delSlab = (id) => {
    if (slabs.length <= 1) { flashAlert("error"); return; }
    setSlabs(p => p.filter(r => r.id !== id));
  };

  const updateSlab = (id, key, val) =>
    setSlabs(p => p.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [key]: val };

      return updated;
    }));

  /* validation */
  // const validate = () => {
  //   const required = ["party", "bookingPlace", "deliveryPlace", "serviceType", "deliveryType"];
  //   const errs = {};
  //   required.forEach(k => { if (!fields[k]) errs[k] = true; });
  //   setErrors(errs);
  //   return Object.keys(errs).length === 0;
  // };

  /* validation */
const validate = () => {
  const required = ["party", "bookingPlace", "deliveryPlace", "serviceType", "deliveryType"];
  const errs = {};
  required.forEach(k => { if (!fields[k]) errs[k] = true; });

  // Rate slab validation — at least one complete weight slab (From, To, Rate) is required
  const hasValidSlab = slabs.some(r =>
    r.fw !== "" && r.tw !== "" && r.rate !== "" &&
    !isNaN(Number(r.fw)) && !isNaN(Number(r.tw)) && !isNaN(Number(r.rate))
  );
  if (!hasValidSlab) {
    errs.slabs = true;
    window.alert("Please enter From Weight, To Weight and Rate for at least one slab in the Rate table before saving.");
  }
  console.log("VALIDATION FAILED ON:", errs, "fields snapshot:", fields);
  setErrors(errs);
  return Object.keys(errs).length === 0;
};

const submitForm = async () => {

  if (!validate()) {
    flashAlert("error");
    return;
  }

  try {

    const finalSlabs = [...slabs];

    // Include current typing row also
    if (
      newSlab.fw ||
      newSlab.tw ||
      newSlab.rate
    ) {
      finalSlabs.push(newSlab);
    }

    const payload = {

      Rate: {

        ...(formMode === "edit" && editingRateId ? { Rate_Def_Id: editingRateId } : {}),

        Status: active ? "A" : "I",

        Effective_From: effFrom || null,

        Effective_To: effTo || null,

        Party_Id:   fields.party  ? parseInt(fields.party)  : null,
        Branch_Id:  fields.branch ? parseInt(fields.branch) : null,
        Job_Id:     fields.job    ? parseInt(fields.job)    : null,

        Currency_Id:
          currencies.find(c =>
            c.Currency_Code === currency
          )?.Currency_Id || null,

        Rate_ValID:
          validities.find(v =>
            v.Validity_Code === fields.validity
          )?.Validity_Id || null,

        Rate_Remark: fields.rateRemarks || null,

        Book_PlaceID: fields.bookingPlace
          ? parseInt(fields.bookingPlace)
          : null,

        Del_PlaceID: fields.deliveryPlace
          ? parseInt(fields.deliveryPlace)
          : null,

        Zone_id: fields.zone
          ? parseInt(fields.zone)
          : null,

        Route_Note: fields.routeNote || null,

        Services_Id:
          serviceTypes.find(s =>
            s.Service_Code === fields.serviceType
          )?.Service_id || null,

        Del_TypeId:
          deliveryTypes.find(d =>
            d.Delivery_Type_Code === fields.deliveryType
          )?.Del_Type_id || null,

        mod_of_Transport:
          transportModes.find(t =>
            t.Transport_Code === fields.transport
          )?.Transport_Id || null,

        // Priority_id: fields.priority || null,
        Priority_id:
        fields.priority === "normal" ? 1 :
        fields.priority === "urgent" ? 2 :
        fields.priority === "overnight" ? 3 :
        fields.priority === "sameday" ? 4 :
        null,

        Min_Charg_Wgt:
          parseFloat(fields.minWt) || null,

        instruction:
          fields.handling || null
      },

      RateDetails: finalSlabs.map(r => ({

        Start_Range:
          parseFloat(r.fw) || 0,

        End_Range:
          parseFloat(r.tw) || 0,

        UOM_Id:
          uomOptions.find(u =>
            u.UOM_Code === r.uom
          )?.Uom_Id || null,

        Rate_Amount:
          parseFloat(r.rate) || 0,

        Add_Weight:
          parseFloat(r.addlWt) || 0
      }))
    };

    console.log("Save Payload:", payload);

    // ── Duplicate check (New Rate only) ──
    if (formMode !== "edit") {
      try {
        // const dupRes = await api.get(
        //   `/CheckDuplicateRate?partyId=${fields.party}&effFrom=${effFrom}&effTo=${effTo}`
        // );
        const dupRes = await api.get(
          `/CheckDuplicateRate?partyId=${fields.party}&branchId=${fields.branch}&bookPlaceId=${fields.bookingPlace}&delPlaceId=${fields.deliveryPlace}&effFrom=${effFrom}&effTo=${effTo}`
        );
        if (dupRes.data?.exists) {
          setAlertMsg("A rate already exists for this party with the same Effective From and To dates. Please change at least one date.");
          setAlertKind("warn");
          showStickyAlert();
          return;
        }
      } catch (_) { /* non-blocking — proceed if check fails */ }
    }

    const endpoint = formMode === "edit" ? "/UpdateRate" : "/SaveRate";
    const response = await api.post(
      endpoint,
      payload,
      {
        headers: {
          "Content-Type": "application/json"
        }
      }
    );

    if (response.data.success) {

      if (formMode === "edit") {
        // Show success toast then switch to search
        setAlertMsg("Rate updated successfully!");
        setAlertKind("success");
        showStickyAlert();
        setFormMode("new");
        setEditingRateId(null);
        setView("search");
      } else {
        flashAlert("success");
        setFormMode("new");
        setEditingRateId(null);
      }

      const [y, n] = rateSeq.split("-");

      setRateSeq(
        y + "-" +
        String(parseInt(n) + 1).padStart(4, "0")
      );

      // CLEAR FORM
  setFields({
    party: "",
    
    validity: "",
    rateRemarks: "",
    bookingPlace: "",
    deliveryPlace: "",
    zone: "",
    routeNote: "",
    serviceType: "",
    deliveryType: "",
    transport: "",
    priority: "",
    minWt: "",
    handling: ""
  });

  setErrors({});

  setCurrency("INR");
  setCurSym("₹");

  setActive(true);

  setEffFrom(todayISO());
  setEffTo(todayISO());

  _rowId = 0;

  setSlabs([makeRow()]);

  setNewSlab({
    fw: "",
    tw: "",
    uom: "",
    rate: "",
    addlWt: "",
    addlRate: ""
  });
    }

  }
  catch (error) {

    console.error("Save Error:", error);

    flashAlert("error");
  }
};

  // Shared reset logic used by both "Discard Changes" and "New Rate"
  const clearFormState = () => {
    setFormMode("new");
    setEditingRateId(null);
    setParties([]); setJobs([]);
    setFields({ branch: "", party: "", job: "", validity: "", rateRemarks: "", bookingPlace: "", deliveryPlace: "", zone: "", routeNote: "", serviceType: "", deliveryType: "", transport: "", priority: "", minWt: "", handling: "" });
    setErrors({});
    setCurrency("INR");
    setCurSym("₹");
    setActive(true);
    setEffFrom(todayISO());
    setEffTo("");
    _rowId = 0;
    // setSlabs([makeRow("0", "5", "KG", "", "1", ""), makeRow("5.01", "10", "KG", "", "1", ""), makeRow("10.01", "25", "KG", "", "1", "")]);
    
    setSlabs([makeRow("", "", "", "", "", "")]);
  };

  const resetForm = () => {
    if (!window.confirm("Discard all changes and reset?")) return;
    clearFormState();
  };

  // Called by the "New Rate" button — always start from a clean slate,
  // regardless of what was previously loaded via Edit/View.
  const startNewRate = () => {
    clearFormState();
    setView("form");
  };

  /* ─── RENDER ─── */
  return (
    <>
      {/* PAGE */}
      <div className="page">

        {/* PAGE HEADER */}
        <div className="page-header">
          <div className="page-header-left">
            <div className="page-title">
              <div className="page-title-icon">📋</div>
              Rate Definition
            </div>
            <div className="page-sub">Configure party-wise, job-wise pricing rates with weight slabs</div>
            {/* {view === "form" && formMode === "new" && (
              <div className="rate-code-pill">⏱ RATE-<span>{rateSeq}</span></div>
            )} */}
            {view === "form" && formMode === "edit" && editingRateId && (
              <div className="rate-code-pill mode-edit">✏ Editing RATE-<span>#{editingRateId}</span></div>
            )}
            {view === "form" && formMode === "view" && editingRateId && (
              <div className="rate-code-pill mode-view">👁 Viewing RATE-<span>#{editingRateId}</span></div>
            )}
          </div>
          <div className="page-header-actions">
            {/* {view === "form" && formMode !== "new" && (
              <button type="button" className="btn-view-toggle btn-back" onClick={() => setView("search")}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back to Search
              </button>
            )} */}
            {view === "form" && (
              <button type="button" className="btn-view-toggle active" onClick={() => setView("search")}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
                Search Records
              </button>
            )}
            {view === "search"  && rights.add && (        
              <button type="button" className="btn-view-toggle active" onClick={startNewRate}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                </svg>
                New Rate
              </button>
            )}
          </div>
        </div>

        {/* ── SEARCH WINDOW ── */}
        {view === "search" && (
          <div className="search-window">
            <div className="search-filter-bar">
              <div className="search-filter-group">
                <label className="search-filter-label">Party / Client</label>
                <ComboBox
                  value={searchFilters.party}
                  onChange={e => setSearchFilters(p => ({ ...p, party: e.target.value }))}
                  // options={[{ id: "", name: "All Parties" }, ...allParties.map(p => ({ id: String(p.Party_id || p.party_id), name: p.Party_Name || p.party_name || "" }))]}
                   options={[{ id: "", name: "All Parties" }, ...allParties.map(p => ({ id: String(p.Party_id || p.party_id || p.Party_Id), name: p.Party_name || p.Party_Name || "" }))]}
                  placeholder="All Parties" valueKey="id" labelKey="name" />
              </div>
              <div className="search-filter-group">
                <label className="search-filter-label">Place</label>
                <ComboBox
                  value={searchFilters.place}
                  onChange={e => setSearchFilters(p => ({ ...p, place: e.target.value }))}
                  options={[{ id: "", name: "All Places" }, ...places.map(pl => ({ id: String(pl.place_id), name: pl.place_Name }))]}
                  placeholder="All Places" valueKey="id" labelKey="name" />
              </div>
              <div className="search-filter-group">
                <label className="search-filter-label">Status</label>
                <ComboBox
                  value={searchFilters.status}
                  onChange={e => setSearchFilters(p => ({ ...p, status: e.target.value }))}
                  options={[{ id: "", name: "All" }, { id: "A", name: "Active" }, { id: "I", name: "Inactive" }]}
                  placeholder="All" valueKey="id" labelKey="name" />
              </div>
              <button type="button" className="btn-search-refresh" onClick={handleSearch} disabled={searchLoading}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ animation: searchLoading ? "spin 1s linear infinite" : "none" }}>
                  <path d="M2 7A5 5 0 1 0 3.5 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                  <path d="M2 1.5v3h3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {searchLoading ? "Loading…" : "Refresh"}
              </button>
            </div>
            <div className="search-table-wrap">
              <table className="search-table">
                <thead>
                  <tr>
                    <th>Sr.No</th>
                    <th style={{display:'none'}}>Rate ID</th>
                    <th>Party</th>
                    <th>Booking Place</th>
                    <th>Delivery Place</th>
                    <th>Eff. From</th>
                    <th>Eff. To</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.length === 0 ? (
                    <tr>
                      <td colSpan="8">
                        <div className="search-empty">
                          <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                            <circle cx="19" cy="19" r="13" stroke="var(--border2)" strokeWidth="2"/>
                            <path d="M29 29l8 8" stroke="var(--border2)" strokeWidth="2.2" strokeLinecap="round"/>
                            <path d="M15 19h8M19 15v8" stroke="var(--border2)" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <p>{searchLoading ? "Loading records…" : <>Select filters and click <strong>Refresh</strong> to view records</>}</p>
                        </div>
                      </td>
                    </tr>
                  // ) : searchResults
                  ) : [...searchResults]
                    .sort((a, b) =>
                      (a.partyName || "").localeCompare(b.partyName || "", undefined, { sensitivity: "base" })
                    )
                      .slice((searchPage - 1) * searchPageSize, searchPage * searchPageSize)
                      .map((r, i) => (
                    <tr key={r.rate_Def_Id || i}>
                      <td>{(searchPage - 1) * searchPageSize + i + 1}</td>
                      <td style={{display:'none'}}><span className="search-id-pill">#{r.rate_Def_Id}</span></td>
                      <td>{r.partyName || r.party_Id || "—"}</td>
                      <td>{places.find(p => p.place_id === r.book_PlaceID)?.place_Name || "—"}</td>
                      <td>{places.find(p => p.place_id === r.del_PlaceID)?.place_Name  || "—"}</td>
                      <td>{r.effective_From ? r.effective_From.split("T")[0] : "—"}</td>
                      <td>{r.effective_To   ? r.effective_To.split("T")[0]   : "—"}</td>
                      <td>
                        <span className={`search-status-badge ${r.status === "A" ? "ss-active" : "ss-inactive"}`}>
                          <span className="sb-dot" />{r.status === "A" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="search-action-btns">
                          <button type="button" className="btn-action btn-view" title="View" onClick={() => handleViewRate(r)}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <ellipse cx="7" cy="7" rx="6" ry="4" stroke="currentColor" strokeWidth="1.5"/>
                              <circle cx="7" cy="7" r="1.8" fill="currentColor"/>
                            </svg>
                          </button>
                          {rights.edit && (
                          <button type="button" className="btn-action btn-edit" title="Edit" onClick={() => handleEditRate(r)}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M9.5 2.5l2 2L4 12H2v-2L9.5 2.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* {searchResults.length > 0 && (() => {
              const totalPages = Math.max(1, Math.ceil(searchResults.length / searchPageSize));
              const startRow = (searchPage - 1) * searchPageSize + 1;
              const endRow = Math.min(searchPage * searchPageSize, searchResults.length);
              const pageNums = Array.from({ length: totalPages }, (_, idx) => idx + 1);
              return (
                <div className="search-pagination">
                  <span className="sp-count">
                    Showing {startRow}–{endRow} of {searchResults.length}
                  </span>
                  <div className="sp-controls">
                    <button type="button" className="sp-btn" disabled={searchPage === 1}
                      onClick={() => setSearchPage(p => Math.max(1, p - 1))}>
                      ‹ Prev
                    </button>
                    {pageNums.map(n => (
                      <button key={n} type="button"
                        className={`sp-btn sp-page${n === searchPage ? " active" : ""}`}
                        onClick={() => setSearchPage(n)}>
                        {n}
                      </button>
                    ))}
                    <button type="button" className="sp-btn" disabled={searchPage === totalPages}
                      onClick={() => setSearchPage(p => Math.min(totalPages, p + 1))}>
                      Next ›
                    </button>
                  </div>
                </div>
              );
            })()} */}

            {searchResults.length > 0 && (() => {
              const totalPages = Math.max(1, Math.ceil(searchResults.length / searchPageSize));
              const startRow = (searchPage - 1) * searchPageSize + 1;
              const endRow = Math.min(searchPage * searchPageSize, searchResults.length);
              const pageItems = getPaginationRange(searchPage, totalPages);

              return (
                <div className="search-pagination">
                  <span className="sp-count">
                    Showing {startRow}–{endRow} of {searchResults.length}
                  </span>
                  <div className="sp-controls">
                    <button type="button" className="sp-btn" disabled={searchPage === 1}
                      onClick={() => setSearchPage(p => Math.max(1, p - 1))}>
                      ‹ Prev
                    </button>
                    {pageItems.map((n, i) =>
                      n === "..." ? (
                        <span key={`ellipsis-${i}`} className="sp-ellipsis">…</span>
                      ) : (
                        <button key={n} type="button"
                          className={`sp-btn sp-page${n === searchPage ? " active" : ""}`}
                          onClick={() => setSearchPage(n)}>
                          {n}
                        </button>
                      )
                    )}
                    <button type="button" className="sp-btn" disabled={searchPage === totalPages}
                      onClick={() => setSearchPage(p => Math.min(totalPages, p + 1))}>
                      Next ›
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── FORM SECTION ── */}
        {view === "form" && (<>

        {/* ALERTS — removed; now using sticky floating alert below */}

        {/* STATUS BAR */}
        <div className="status-bar">
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--muted)", marginBottom: 3 }}>
              Status
            </div>
            <div className={`status-badge${active ? " sb-active" : " sb-inactive"}`}>
              <span className="sb-dot" />
              {active ? " Active" : " Inactive"}
            </div>
          </div>
          <div className="status-bar-sep" />
          <div className="date-field">
            <span className="date-label">Effective From</span>
            <input type="date" className="date-input" value={effFrom} onChange={e => setEffFrom(e.target.value)} />
          </div>
          <div className="date-field">
            <span className="date-label">Effective To</span>
            <input type="date" className="date-input" value={effTo} onChange={e => setEffTo(e.target.value)} />
          </div>
          <div className="toggle-wrap">
            <span className="toggle-label">Activate Rate</span>
            <label className="toggle">
              <input type="checkbox" checked={active} onChange={e => handleToggle(e.target.checked)} />
              <span className="toggle-sl" />
            </label>
          </div>
        </div>

        {/* ── VIEW MODE BANNER ── */}
        {formMode === "view" && (
          <div className="view-mode-banner">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M7.5 5v3.5M7.5 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            You are in <strong>View Mode</strong> — fields are read-only.
            {rights.edit && (
            <button type="button" className="btn-view-to-edit" onClick={() => setFormMode("edit")}>
              Switch to Edit
            </button>
            )}
          </div>
        )}

        <div className={formMode === "view" ? "form-readonly" : ""}>

        {/* ── CARD 1: PARTY & JOB ── */}
        <Card id="c1" num="1" title="Party & Job" sub="Select branch, party and job for this rate"
          collapsed={collapsed.c1} onToggle={() => toggleCard("c1")}>
          <div className="fg fg-4">

            {/* Branch */}
            <div className="field">
              <label className="req">Branch</label>
              <ComboBox id="branch" value={fields.branch}
                onChange={handleBranchChange}
                options={branches} placeholder="Select branch…"
                className={errors.branch ? "err" : ""}
                disabled={formMode === "view"}
                valueKey="Branch_id" labelKey="Branch_Name" />
            </div>

            {/* Party — depends on Branch */}
            <div className="field">
              <label className="req">Party / Client</label>
              <ComboBox id="party" value={fields.party}
                onChange={handlePartyChange}
                options={parties}
                placeholder={fields.branch ? "Select party…" : "Select branch first…"}
                className={errors.party ? "err" : ""}
                disabled={formMode === "view" || !fields.branch}
                valueKey="Party_id" labelKey="Party_Name" />
            </div>

            {/* Job — depends on Party */}
            <div className="field">
              <label>Job</label>
              <ComboBox id="job" value={fields.job}
                onChange={handleField("job")}
                options={jobs}
                placeholder={fields.party ? "Select job…" : "Select party first…"}
                disabled={formMode === "view" || !fields.party}
                valueKey="job_id" labelKey="job_name" />
            </div>

            {/* Currency */}
            <div className="field">
              <label>Currency</label>
              <ComboBox id="currency" value={currency}
                onChange={e => handleCurrency(e.target.value)}
                options={currencies.map(c => ({ id: c.Currency_Code, name: `${c.Currency_Code} — ${c.Currency_Name} (${c.Currency_Symbol})` }))}
                placeholder="Select currency…"
                disabled={formMode === "view"}
                valueKey="id" labelKey="name" />
            </div>

            {/* Rate Validity */}
            <div className="field">
              <label>Rate Validity</label>
              <ComboBox id="validity" value={fields.validity}
                onChange={handleField("validity")}
                options={validities.map(v => ({ id: v.Validity_Code, name: v.Validity_Name }))}
                placeholder="Select validity…"
                disabled={formMode === "view"}
                valueKey="id" labelKey="name" />
            </div>

            {/* Rate Remarks */}
            <div className="field">
              <label>Rate Remarks</label>
              <input type="text" id="rateRemarks" placeholder="e.g. Special negotiated rate"
                value={fields.rateRemarks} onChange={handleField("rateRemarks")} />
            </div>

          </div>
        </Card>

        {/* ── CARD 2: LOCATIONS ── */}
        <Card id="c2" num="2" title="Booking & Delivery Location"
          sub="Origin (booking place) and destination (delivery place)"
          collapsed={collapsed.c2} onToggle={() => toggleCard("c2")}>
          <div className="fg fg-2">
            <div className="field">
              <label className="req">Booking Place (Origin)</label>
              <CitySelect id="bookingPlace" required value={fields.bookingPlace}
                onChange={handleField("bookingPlace")}
                // options={bookingPlaces}
                options={places}
                 placeholder="Select origin city…"
                disabled={formMode === "view"}
                className={errors.bookingPlace ? "err" : ""} />
            </div>
            <div className="field">
              <label className="req">Delivery Place (Destination)</label>
              <CitySelect id="deliveryPlace" required value={fields.deliveryPlace}
                onChange={handleField("deliveryPlace")}
                // options={deliveryPlaces} 
                options={places}
                placeholder="Select destination city…"
                disabled={formMode === "view"}
                className={errors.deliveryPlace ? "err" : ""} />
            </div>
          </div>

          <div className="fg fg-2" style={{ marginTop: 14 }}>
            <div className="field">
              <label>Route / Zone</label>
              <ComboBox id="zone" value={fields.zone}
                onChange={handleField("zone")}
                options={(zones || []).map(z => ({ id: String(z.Zone_Id), name: z.Zone_Name }))}
                placeholder="Select Zone…"
                disabled={formMode === "view"}
                valueKey="id" labelKey="name" />
            </div>
            <div className="field">
              <label>Route Notes</label>
              <input type="text" id="routeNote" placeholder="e.g. Via NH-48, Hill surcharge applies…"
                value={fields.routeNote} onChange={handleField("routeNote")} />
            </div>
          </div>
        </Card>

        {/* ── CARD 3: SERVICE & DELIVERY ── */}
        <Card id="c3" num="3" title="Service & Delivery Type"
          sub="Service mode, delivery type and transport configuration"
          collapsed={collapsed.c3} onToggle={() => toggleCard("c3")}>
          <div className="fg fg-3">
            <div className="field">
              <label className="req">Service Type</label>
              <ComboBox id="serviceType" value={fields.serviceType}
                onChange={handleField("serviceType")}
                options={serviceTypes.map(s => ({ id: s.Service_Code, name: s.Services }))}
                placeholder="Select service type…"
                className={errors.serviceType ? "err" : ""}
                disabled={formMode === "view"}
                valueKey="id" labelKey="name" />
            </div>
            <div className="field">
              <label className="req">Delivery Type</label>
              <ComboBox id="deliveryType" value={fields.deliveryType}
                onChange={handleField("deliveryType")}
                options={deliveryTypes.map(d => ({ id: d.Delivery_Type_Code, name: d.Delivery_Type_Name }))}
                placeholder="Select delivery type…"
                className={errors.deliveryType ? "err" : ""}
                disabled={formMode === "view"}
                valueKey="id" labelKey="name" />
            </div>
            <div className="field">
              <label>Mode of Transport</label>
              <ComboBox id="transport" value={fields.transport}
                onChange={handleField("transport")}
                options={transportModes.map(t => ({ id: t.Transport_Code, name: t.Transport_Name }))}
                placeholder="Select mode…"
                disabled={formMode === "view"}
                valueKey="id" labelKey="name" />
            </div>
          </div>

          <div className="fg fg-3" style={{ marginTop: 14 }}>
            <div className="field">
              <label>Priority Level</label>
              <ComboBox id="priority" value={fields.priority}
                onChange={handleField("priority")}
                options={[
                  { id: "normal",    name: "Normal" },
                  { id: "urgent",    name: "Urgent" },
                  { id: "overnight", name: "Overnight" },
                  { id: "sameday",   name: "Same Day" },
                ]}
                placeholder="Select priority…"
                disabled={formMode === "view"}
                valueKey="id" labelKey="name" />
            </div>
            <div className="field">
              <label>Min. Chargeable Weight</label>
              <input type="number" id="minWt" placeholder="e.g. 0.5" min="0" step="0.01"
                value={fields.minWt} onChange={handleField("minWt")} />
            </div>
            <div className="field">
              <label>Special Handling Instructions</label>
              <input type="text" id="handling" placeholder="e.g. No stacking, fragile on top"
                value={fields.handling} onChange={handleField("handling")} />
            </div>
          </div>
        </Card>

        {/* ── CARD 4: WEIGHT SLABS ── */}
        <Card id="c4" num="4" title="Weight Slabs & Rates"
          sub="Tiered weight-based pricing — from weight, to weight, rate and additional charge"
          collapsed={collapsed.c4} onToggle={() => toggleCard("c4")}>
          <div style={{ paddingBottom: 0 }}>
            <div className="slab-wrap">
              <table className="slab-table">
                <thead>
                  <tr>
                    <th style={{ width: 52 }}>#</th>
                    <th>From Weight</th>
                    <th>To Weight</th>
                    <th style={{ width: 100 }}>UOM</th>
                    <th>Each Addl. Weight</th>
                    <th>Rate</th>
                    <th style={{ width: 52 }}></th>
                  </tr>
                </thead>
                <tbody onKeyDown={handleSlabEnter}>
                  {slabs.map((row, i) => (
                    <tr className="slab-row" key={row.id}>
                      <td><span className="slab-num">{String(i + 1).padStart(2, "0")}</span></td>
                      <td><input className="sl-inp fw" type="number" placeholder="0.00" min="0" step="0.01"
                        value={row.fw} onChange={e => updateSlab(row.id, "fw", e.target.value)} /></td>
                      <td><input className="sl-inp tw" type="number" placeholder="0.00" min="0" step="0.01"
                        value={row.tw} onChange={e => updateSlab(row.id, "tw", e.target.value)} /></td>
                      <td>
                        <select className="sl-sel uom" value={row.uom}
                          onChange={e => updateSlab(row.id, "uom", e.target.value)}
                          disabled={formMode === "view"}>
                          {uomOptions.map(u => (
                            <option key={u.Uom_Id} value={u.UOM_Code}>{u.UOM_Code}</option>
                          ))}
                        </select>
                      </td>
                      <td><input className="sl-inp addlwt" type="number" placeholder="0.00" min="0" step="0.01"
                        value={row.addlWt} onChange={e => updateSlab(row.id, "addlWt", e.target.value)} /></td>
                      <td className="rate-td">
                        <span className="cur-sym">{curSym}</span>
                        <input className="sl-inp rate" type="number" placeholder="0.00" min="0" step="0.01"
                          value={row.rate} onChange={e => updateSlab(row.id, "rate", e.target.value)}
                          style={{ paddingLeft: 22 }} />
                      </td>
                      <td>
                        <button type="button" className="btn-row-del" title="Delete row"
                          onClick={() => delSlab(row.id)}>×</button>
                      </td>
                    </tr>
                  ))}

                  {/* ── ADD ROW ── */}
                  <tr className="slab-row new-row">
                    <td><span className="slab-num new">+</span></td>
                    <td><input className="sl-inp fw" type="number" placeholder="0.00" min="0" step="0.01"
                      value={newSlab.fw}
                      onChange={e => {
                        setNewSlab(p => ({ ...p, fw: e.target.value }));
                      }} /></td>
                    <td><input className="sl-inp tw" type="number" placeholder="0.00" min="0" step="0.01"
                      value={newSlab.tw}
                      onChange={e => {
                        setNewSlab(p => ({ ...p, tw: e.target.value }));
                      }} /></td>
                    <td>
                      <select className="sl-sel uom" value={newSlab.uom}
                        onChange={e => setNewSlab(p => ({ ...p, uom: e.target.value }))}
                        disabled={formMode === "view"}>
                        <option value="">UOM</option>
                        {uomOptions.map(u => (
                          <option key={u.Uom_Id} value={u.UOM_Code}>{u.UOM_Code}</option>
                        ))}
                      </select>
                    </td>
                    <td><input className="sl-inp addlwt" type="number" placeholder="0.00" min="0" step="0.01"
                      value={newSlab.addlWt}
                      onChange={e => setNewSlab(p => ({ ...p, addlWt: e.target.value }))} /></td>
                    <td className="rate-td">
                      <span className="cur-sym">{curSym}</span>
                      <input className="sl-inp rate" type="number" placeholder="0.00" min="0" step="0.01"
                        value={newSlab.rate}
                        onChange={e => setNewSlab(p => ({ ...p, rate: e.target.value }))}
                        style={{ paddingLeft: 22 }} />
                    </td>
                    <td>
                      <button type="button" className="btn-add-slab" title="Add slab" onClick={addSlab}
                        style={{ padding: "4px 10px", fontSize: 18, lineHeight: 1 }}>
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                          <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="slab-footer">
                <span className="slab-count">{summary.count} slab{summary.count !== 1 ? "s" : ""} defined</span>
              </div>
            </div>
          </div>
        </Card>

        </div>{/* /form-readonly */}

        {/* FOOTER */}
        <div className="form-footer">

  {formMode !== "view" && (
  <button
    type="button"
    className="btn btn-ghost"
    onClick={resetForm}
  >
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M2 6.5A4.5 4.5 0 1 0 3 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 1.5v3h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    Discard Changes
  </button>
  )}

  {formMode !== "view" && (
  <button
    type="button"
    className="btn btn-primary"
    onClick={submitForm}
  >
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M2 6.5l3.5 3.5 5.5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    {formMode === "edit" ? "Update Rate" : "Save Rate"}
  </button>
  )}
  {/* {formMode === "view" && rights.edit &&(
  <button
    type="button"
    className="btn btn-outline"
    onClick={() => { setFormMode("edit"); }}
  >
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M8.5 2l2.5 2.5L4 11H1.5v-2.5L8.5 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
    Edit This Rate
  </button>
  )} */}

</div>

        </>)}

      </div>
      {/* ── STICKY FLOATING ALERT — rendered via portal to document.body so position:fixed always works ── */}
      {alertShow && ReactDOM.createPortal(
        <div className={`sticky-alert sa-${alertKind}`}>
          {alertKind === "success" && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.6"/>
              <path d="M5.5 9l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {alertKind === "error" && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.6"/>
              <path d="M9 5.5v4M9 12v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          )}
          {alertKind === "warn" && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 2L16.5 15H1.5L9 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
              <path d="M9 7v4M9 12.5v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          )}
          <span>{alertMsg}</span>
          <button className="sa-close" onClick={() => setAlertShow(false)}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>,
        document.body
      )}
    </>
  );
}